import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { captureException } from "@freightsimple/observability/errorReporting";
import { delay } from "@/Helpers/delay";

// Waits before the 2nd and 3rd attempts, so a dropped mobile connection has a
// few seconds to come back.
const RETRY_DELAYS_MS = [1000, 3000];

// `Elements` reads null as "Stripe isn't loaded" - Stripe's contract, so the
// annotation makes the null legal (same exception as stripeElementsDecorators).
const stripeNotLoaded: Stripe | null = null;

/**
 * Loads Stripe.js, trying again if the script fails to download. stripe-js
 * clears its own cache on failure, so each call injects the script afresh.
 *
 * Never rejects: after the last failed attempt it reports once and resolves
 * to null, so card payment is unavailable but nothing goes unhandled.
 */
export async function loadStripeWithRetry(
  publishableKey: string,
): Promise<Stripe | null> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await loadStripe(publishableKey);
    } catch (e: unknown) {
      const wait = RETRY_DELAYS_MS[attempt];
      if (wait === undefined) {
        captureException(e, {
          stripe: "load-failed",
          attempts: String(attempt + 1),
        });
        return stripeNotLoaded;
      }
      await delay(wait);
    }
  }
}
